// @spec GHG-STR-108
/**
 * TanStack Query hooks for Scope 1/2 activity assignment endpoints.
 */

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ActivityAssignmentConfirmRequest,
  ActivityAssignmentFinalizeRequest,
  ActivityAssignmentJobResponse,
  ActivityAssignmentJobCreateRequest,
  ActivityAssignmentQueueFilters,
  ActivityAssignmentSuggestRequest,
} from '../types/activity-assignment';

import {
  confirmActivityAssignment,
  createActivityAssignmentJob,
  finalizeActivityAssignmentQueueItem,
  getActivityAssignmentJob,
  getActivityAssignmentQueue,
  suggestActivityAssignment,
} from './activity-assignment';
import { useGHGOrganizationId } from './use-ghg-organization-id';

export const activityAssignmentKeys = {
  all: ['ghg-activity-assignment'] as const,
  queue: (organizationId: string, filters: ActivityAssignmentQueueFilters) =>
    [...activityAssignmentKeys.all, 'queue', organizationId, filters] as const,
  job: (organizationId: string, jobId: string) =>
    [...activityAssignmentKeys.all, 'job', organizationId, jobId] as const,
};

export function useSuggestActivityAssignment() {
  const { getToken } = useAuth();
  const organizationId = useGHGOrganizationId();

  return useMutation({
    mutationFn: async (data: ActivityAssignmentSuggestRequest) => {
      if (!organizationId) throw new Error('Organization not found');
      return suggestActivityAssignment(getToken, organizationId, data);
    },
  });
}

export function useConfirmActivityAssignment() {
  const { getToken } = useAuth();
  const organizationId = useGHGOrganizationId();

  return useMutation({
    mutationFn: async (data: ActivityAssignmentConfirmRequest) => {
      if (!organizationId) throw new Error('Organization not found');
      return confirmActivityAssignment(getToken, organizationId, data);
    },
  });
}

export function useActivityAssignmentQueue(filters: ActivityAssignmentQueueFilters) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const query = useQuery({
    queryKey: activityAssignmentKeys.queue(organizationId ?? '', filters),
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization not found');
      return getActivityAssignmentQueue(getToken, organizationId, filters);
    },
    enabled: (isSignedIn ?? false) && !!organizationId,
    staleTime: 30 * 1000,
  });

  return {
    items: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    page: query.data?.page ?? (filters.page ?? 1),
    pageSize: query.data?.pageSize ?? (filters.pageSize ?? 25),
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreateActivityAssignmentJob() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = useGHGOrganizationId();

  return useMutation({
    mutationFn: async (data: ActivityAssignmentJobCreateRequest) => {
      if (!organizationId) throw new Error('Organization not found');
      return createActivityAssignmentJob(getToken, organizationId, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: activityAssignmentKeys.all });
    },
  });
}

export function useActivityAssignmentJob(
  jobId: string | null,
  options?: { enabled?: boolean; refetchIntervalMs?: number }
) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  return useQuery<ActivityAssignmentJobResponse>({
    queryKey: activityAssignmentKeys.job(organizationId ?? '', jobId ?? ''),
    queryFn: async () => {
      if (!organizationId || !jobId) throw new Error('Assignment job not found');
      return getActivityAssignmentJob(getToken, organizationId, jobId);
    },
    enabled: (isSignedIn ?? false) && !!organizationId && !!jobId && (options?.enabled ?? true),
    ...(options?.refetchIntervalMs !== undefined
      ? { refetchInterval: options.refetchIntervalMs }
      : {}),
    staleTime: 15 * 1000,
  });
}

export function useFinalizeActivityAssignmentQueueItem() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = useGHGOrganizationId();

  return useMutation({
    mutationFn: async ({ itemId, data }: { itemId: string; data: ActivityAssignmentFinalizeRequest }) => {
      if (!organizationId) throw new Error('Organization not found');
      return finalizeActivityAssignmentQueueItem(getToken, organizationId, itemId, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: activityAssignmentKeys.all });
    },
  });
}
